import {
    getState,
    setState,
    subscribeToStateChange,
} from './state';

import {
    setPlayerOddsFormat,
    getClientSettings,
} from '~spa/Utils/WrapperIntegration/Ucf';

import {
    getGeoCheckTimeout,
} from '~spa/Utils/SiteConfig';

import { POKER_BLAST_WIDGET } from '~spa/Components/PokerBlastWidget/constants';

import {
    DEFAULT_LOCALE,
    GIBRALTAR_REGULATION_ID,
    GERMAN_REGULATION_ID,
    MALTA_REGULATION_ID,
} from './constants';
import {
    getLocaleLang,
    getBrandLang,
} from '~shared/locales/utils/utils';

export const USER_BALANCES_KEY = 'wrapper:customer:balances';
export const USER_LOCALE_STATE_KEY = 'customer:locale';
export const USER_ODDS_FORMAT_STATE_KEY = 'customer:odds-format';
export const USER_THEME_MODE_STATE_KEY = 'customer:theme_mode';
export const USER_LOGGED_IN_STATE_KEY = 'customer:is_logged_in';
export const USER_HAS_KAMBI_BETS = 'wrapper:hasKambiBets';
export const USER_ID = 'customer:id';
export const USER_INSTRUCTION_CARDS = 'customer:instructionCards';
export const USER_FAVOURITE_SPORTS = 'customer:favouriteSports';
export const USER_IS_FIRST_LOGIN = 'customer:isFirstLogin';
export const USER_EACHWAY_ALLOWED = 'customer:isEachwayAllowed';
export const USER_BOG_RESTRICTION = 'customer:restrict_bog';
export const USER_DEVICE_STATE_KEY = 'customer:device';
export const USER_COUNTRY_ISO_STATE_KEY = 'country:iso';
export const USER_COUNTRYSTATE_STATE_KEY = 'country:state';
export const USER_CURRENCY_ISO_STATE_KEY = 'currency:iso';
export const USER_LANGUAGE_ISO_STATE_KEY = 'language:iso';
export const USER_ACCEPT_ODDS_CHANGE_KEY = 'customer:acceptOddsChange';
export const USER_CONFIRM_CASHOUT_KEY = 'customer:confirmCashout';
export const USER_TIMEZONE_STATE_KEY = 'timezone';
export const BRAND_ID_STATE_KEY = 'wrapper:brandId';
export const SUB_BRAND_ID_STATE_KEY = 'wrapper:subBrandId';
export const PRODUCT_PACKAGE_ID_STATE_KEY = 'wrapper:productPackageId';
export const REGULATION_TYPE_ID_STATE_KEY = 'wrapper:regulationTypeId';
export const REGULATION_TYPE_DATA_STATE_KEY = 'customer:regulation_type_data';
export const REGULATION_TAX_ON_RETURNS_KEY = 'tax_on_returns';
export const REGULATION_BETBUILDER_REGION_KEY = 'betbuilder_region';
export const REGULATION_LOGIN_RESTRICTED_KEY = 'login_restricted';
export const ONBOARDING_PAGE_ENABLED_KEY = 'onboardingPage:enabled';
export const USER_MODE_STATE_KEY = 'wrapper:userMode';
export const WRAPPER_IS_MOBILE_STATE_KEY = 'wrapper:isMobile';
export const WRAPPER_IS_HYBRID_STATE_KEY = 'wrapper:isHybrid';
export const WRAPPER_IS_FTD_STATE_KEY = 'wrapper:isFtd';
export const WRAPPER_IS_PROMOTION_PERMITTED_STATE_KEY = 'wrapper:isPromotionPermitted';
export const WRAPPER_IS_PROMOTION_RESTRICTED_STATE_KEY = 'wrapper:isPromotionRestricted';
export const WRAPPER_IS_VIP_STATE_KEY = 'wrapper:isVip';
export const WRAPPER_RMS_STATE_KEY = 'wrapper:rms';
export const WRAPPER_SERIAL_STATE_KEY = 'wrapper:serial';
export const WRAPPER_OS_NAME = 'wrapper:osName';
export const WRAPPER_OS_VERSION = 'wrapper:osVersion';
export const RESTRICTED_APPS_KEY = 'wrapper:restrictedApps';
export const WRAPPER_PAGE_CORRELATION_ID_STATE_KEY = 'wrapper:pageCorrelationId';
export const WRAPPER_PRDC_MESSAGE_RECIVED_KEY = 'wrapper:didReceivePRDCMessage';
export const WRAPPER_GEO_CHECK_STATUS_KEY = 'wrapper:geocheck:status';

/**
 * Get the user's odds format
 * @return {String} the user's odds format
 */
export const getOddsFormat = () => getState(USER_ODDS_FORMAT_STATE_KEY);

/**
 * Sets the user's odds format and notifies the Wrapper about it
 * @param {String} value   Odds Format
 * @return {void}
 */
export const setOddsFormat = (value) => {
    setState(USER_ODDS_FORMAT_STATE_KEY, value);
    setPlayerOddsFormat(value);
};

/**
 * Fires the callback when odds format in state changed.
 * @param {Function} callback   Function which is run when state changed.
 * @return {void}
 */
export const onOddsFormatChange = (callback) => subscribeToStateChange(
    USER_ODDS_FORMAT_STATE_KEY,
    callback
);

/** Get the user's current theme
* @return {Integer} the user's current theme
*/
 export const getCurrentTheme = () => getState(USER_THEME_MODE_STATE_KEY) || 0;

 /**
 * Fires the callback when theme in state changed.
 * @param {Function} callback   Function which is run when state changed.
 * @return {void}
 */
export const onThemeChange = (callback) => subscribeToStateChange(
    USER_THEME_MODE_STATE_KEY,
    callback
);

/**
 * Sets current theme and notifies the Wrapper about it
 * @param {Integer} mode   theme
 * @return {void}
 */
 export const setCurrentTheme = (mode) => {
    setState(USER_THEME_MODE_STATE_KEY, mode);
};

/**
 * Get the user's country ISO code
 *
 * NB: If you are passing this to PHP/BE, you should use getCountryIso() in
 * application/libraries/auth.php instead.
 *
 * @return {String} The user's country ISO code
 */
export const getCountryIso = () => getState(USER_COUNTRY_ISO_STATE_KEY).toUpperCase();

/**
 * Get the user's (country) state provided by Wrapper
 *
 * @return {String} State
 */
 export const getCountryState = () => getState(USER_COUNTRYSTATE_STATE_KEY);

/**
 * Get the user's locale
 * @return {String} The user's locale
 */
export const getLocale = () => getState(USER_LOCALE_STATE_KEY) || DEFAULT_LOCALE;

/**
 * Get locale data from Wrapper.
 * Important! The data source is UCF.
 *
 * @return {String} The user's locale
 */
export const getWrapperLocale = () => {
    const { locale } = getClientSettings();

    return locale;
};

/**
 * Get the user's currency ISO code
 * @return {String} The user's currency ISO code
 */
export const getCurrencyIso = () => getState(USER_CURRENCY_ISO_STATE_KEY).toUpperCase();

/**
 * Get the user's browsing country (Country of IP) ISO code.
 * Important! The data source is UCF.
 *
 * @return {String} The user's browsing country ISO code
 */
export const getBrowsingCountryIso = () => {
    const { country: browsingCountryIso } = getClientSettings();

    return browsingCountryIso.toUpperCase();
};

/**
 * Get the user's language ISO code
 * @return {String} The user's language ISO code
 */
export const getLanguageIso = () => getState(USER_LANGUAGE_ISO_STATE_KEY).toLowerCase();

/**
 * Get the user's custom language 3 letter code
 * @return {String} The user's custom language 3 letter code
 */
export const getLanguageCode = () => {
    const brandId = getBrandId();
    const langIso = getLanguageIso();
    const brandLang = getBrandLang(brandId, langIso);

    if (brandLang) {

        return brandLang;
    }

    const localeLang = getLocaleLang();

    if (localeLang) {

        return localeLang;
    }

    return langIso;
};

/**
 * Get the user's timezone
 * @return {String} The user's timezone
 */
export const getTimezone = () => getState(USER_TIMEZONE_STATE_KEY);

/**
 * Checks if the userr is logged in
 * @return {Boolean} Returns true if the user is logged in or false otherwise
 */
export const isUserLoggedIn = () => getState(USER_LOGGED_IN_STATE_KEY);

/**
 * Get the user's id
 * @return {String} The user's id
 */
export const getCustomerId = () => getState(USER_ID);

/**
 * Sets user as logged in one and populates the customer Id state.
 * @param {Integer} customerId   Customer Id
 * @return {void}
 */
export const setUserLoggedIn = (customerId) => {
    setState(USER_ID, customerId);
    setState(USER_LOGGED_IN_STATE_KEY, true);
    setState(USER_MODE_STATE_KEY, 'Authenticated');
};

/**
 * Sets user as logged out
 * @param {Integer} customerId   Customer Id
 * @return {void}
 */
export const setUserLoggedOut = () => {
    setState(USER_ID, undefined);
    setState(USER_LOGGED_IN_STATE_KEY, false);
    setState(USER_MODE_STATE_KEY, 'Anonymous');
    setState(USER_HAS_KAMBI_BETS, false);
};

/**
 * Get the user's Brand ID
 * @return {Integer} The user's Brand Id
 */
export const getBrandId = () => getState(BRAND_ID_STATE_KEY);

/**
 * Get the user's Sub-Brand ID
 * @return {Integer} The user's Sub-Brand Id
 */
export const getSubBrandId = () => getState(SUB_BRAND_ID_STATE_KEY);

/**
 * Get the user's Product Package ID
 * @return {Integer} The user's Product Package ID
 */
export const getProductPackageId = () => getState(PRODUCT_PACKAGE_ID_STATE_KEY);

/**
 * Get the user's Regulation Type ID
 * See NAME-ID map details on mysql sportsbook.regulation_types table
 * @return {Integer} The user's Regulation Type ID
 */
export const getRegulationTypeId = () => {
    // @todo: Enforcement for Germany should be removed when bug in wrapper is fixed
    // https://dedsert.atlassian.net/browse/OR-5467
    const userIsLoggedIn = isUserLoggedIn();
    const countryIso = getCountryIso();
    const currentRegulationId = getState(REGULATION_TYPE_ID_STATE_KEY);
    if (
        !userIsLoggedIn
        && countryIso === 'DEU'
        && (currentRegulationId === GIBRALTAR_REGULATION_ID
                || currentRegulationId === MALTA_REGULATION_ID
            )
        ) {

        return GERMAN_REGULATION_ID;
    }

    return currentRegulationId;
};

/**
 * Fires a callback user's Regulation Type ID changes
 * @param {Function} callback   Function which is run when state changed.
 * @return {void}
 */
export const onRegulationTypeIdChange = (callback) => subscribeToStateChange(
    REGULATION_TYPE_ID_STATE_KEY,
    callback
);

/**
 * Get the user's mode which is set by Wrapper.
 * Can be either Authenticated or Anonymous.
 * @return {String} The user's mode
 */
export const getUserMode = () => getState(USER_MODE_STATE_KEY);

/**
 * Fires the callback when user logged in state changed.
 * @param {Function} callback   Function which is run when customer state changed.
 * @return {void}
 */
export const onUserModeChange = (callback) => subscribeToStateChange(
    USER_LOGGED_IN_STATE_KEY,
    callback
);

/**
 * Get the segmentation isMobile flag provided by Wrapper
 * @return {Boolean} The segmentation's isMobile flag
 */
export const getIsMobileWrapperFlag = () => getState(WRAPPER_IS_MOBILE_STATE_KEY);

/**
 * Get the segmentation isHybrid flag provided by Wrapper
 * This should be true for the Spectate (ios/android) apps
 *
 * 2021-11-03 Careful now! depending on what wrapper is in use (SI, 888) this function might not work. You
 * can verify this by looking at bb-client-state after the SPA has loaded, but this requires using debugging
 * tools with the app, i.e. mobile dev. Instead use getUserOsName() which is only populated for the mobile
 * app.
 * @return {Boolean} The segmentation's isHybrid flag
 */
export const getIsHybridWrapperFlag = () => getState(WRAPPER_IS_HYBRID_STATE_KEY);

/**
 * Get the user's OS name provided by the Wrapper
 * Only provided from ios/android app - these come from the app itself and are passed through the wrapper.
 * 2021-11-06 DO NOT rely on this to detect anything but Spectate android/ios app. There is no platform detection done
 * on the UC at time of writing.
 * @return {String|null} The OS name or null if doesn't exist
 */
export const getUserOsName = () => {
    let osName = getState(WRAPPER_OS_NAME);

    return osName ? osName.toLowerCase() : osName;
};

/**
 * Get the user's device provided by the wrapper.
 * @returns {'PHONE' | 'TABLET' | 'PC' | 'PHABLET' | 'UNKNOWN'} a string representation of the users device
 */
export const getUserDevice = () => {
    const device = getState(USER_DEVICE_STATE_KEY);
    const devicesMap = {
        1:'PHONE',
        2:'TABLET',
        3:'PC',
        4:'PHABLET',
    };

    return devicesMap[device] || 'UNKNOWN';
};

/**
 * Check if we are on iOS app
 * Note that this is dependent on app pushing the osName to the UC which then sends it back to us
 * @returns {Boolean} true|false
 */
 export const isIosApp = () => getUserOsName() === 'ios';

/**
 * Check if we are on Android app
 * Note that this is dependent on app pushing the osName to the UC which then sends it back to us
 * @returns {Boolean} true|false
 */
 export const isAndroidApp = () => getUserOsName() === 'android';

/**
 * Get the segmentation isFtd (First Time Deposit) flag provided by Wrapper
 * It's important to know that isFTD flag isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains isFTD flag.
 *
 * @return {Boolean|null} The segmentation's isFtd flag
 */
export const getIsFtdWrapperFlag = () => {
    const isFtd = getState(WRAPPER_IS_FTD_STATE_KEY);

    if (typeof isFtd === 'undefined') {
        return null;
    }

    return isFtd;
};

/**
 * Get the segmentation isPromotionPermitted flag provided by Wrapper
 * It's important to know that isPromotionPermitted flag
 * isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains isPromotionPermitted flag.
 *
 * @return {Boolean|null} The segmentation's isPromotionPermitted flag
 */
export const getIsPromotionPermittedWrapperFlag = () => {
    const isPromotionPermitted = getState(
        WRAPPER_IS_PROMOTION_PERMITTED_STATE_KEY
    );

    if (typeof isPromotionPermitted === 'undefined') {
        return null;
    }

    return isPromotionPermitted;
};

/**
 * Get the segmentation isPromotionRestricted flag provided by Wrapper
 * It's important to know that isPromotionRestricted flag
 * isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains isPromotionRestricted flag.
 *
 * According to info given by Ad-Tech Team,
 * this flag is will be used only is Spain Regulated market only.
 *
 * @return {Boolean|null} The segmentation's isPromotionRestricted flag
 */
export const getIsPromotionRestrictedWrapperFlag = () => {
    const isPromotionRestricted = getState(
        WRAPPER_IS_PROMOTION_RESTRICTED_STATE_KEY
    );

    if (typeof isPromotionRestricted === 'undefined') {
        return null;
    }

    return isPromotionRestricted;
};

/**
 * Get the segmentation isVIP flag provided by Wrapper
 * It's important to know that isVIP flag isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains isVIP flag.
 *
 * @return {Boolean|null} The segmentation's isVIP flag
 */
export const getIsVipWrapperFlag = () => {
    const isVip = getState(WRAPPER_IS_VIP_STATE_KEY);

    if (typeof isVip === 'undefined') {
        return null;
    }

    return isVip;
};


/**
 * Get the segmentation RMS provided by Wrapper
 * It's important to know that RMS
 * isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains RMS.
 *
 * @return {Integer|null} The segmentation's RMS
 */
 export const getRmsWrapperData = () => {
    const serial = getState(WRAPPER_RMS_STATE_KEY);

    if (typeof serial === 'undefined') {
        return null;
    }

    return serial;
};

/**
 * Get the segmentation serial number provided by Wrapper
 * It's important to know that serial number
 * isn't part of the clientSettings.
 * It might be undefined until we received player segmentation update which
 * contains serial number.
 *
 * @return {Integer|null} The segmentation's serial number
 */
export const getSerialWrapperData = () => {
    const serial = getState(WRAPPER_SERIAL_STATE_KEY);

    if (typeof serial === 'undefined') {
        return null;
    }

    return serial;
};

/**
 * Fires the callback when isFTD in state changed.
 * @param {Function} callback   Function which is run when isFTD state changed.
 * @return {void}
 */
export const onIsFtdFlagChange = (callback) => subscribeToStateChange(
    WRAPPER_IS_FTD_STATE_KEY,
    callback
);

/**
 * Fires the callback when isPromotionPermitted in state changed.
 * @param {Function} callback   Function which is run when isPromotionPermitted state changed.
 * @return {void}
 */
export const onIsPromotionPermittedFlagChange = (callback) => subscribeToStateChange(
    WRAPPER_IS_PROMOTION_PERMITTED_STATE_KEY,
    callback
);

/**
 * Fires the callback when isPromotionRestricted in state changed.
 * @param {Function} callback   Function which is run when isPromotionRestricted state changed.
 * @return {void}
 */
export const onIsPromotionRestrictedFlagChange = (callback) => subscribeToStateChange(
    WRAPPER_IS_PROMOTION_RESTRICTED_STATE_KEY,
    callback
);

/**
 * Fires the callback when isVIP in state changed.
 * @param {Function} callback   Function which is run when isVIP state changed.
 * @return {void}
 */
export const onIsVipFlagChange = (callback) => subscribeToStateChange(
    WRAPPER_IS_VIP_STATE_KEY,
    callback
);

/**
 * Fires the callback when RMS in state changed.
 * @param {Function} callback   Function which is run when RMS state changed.
 * @return {void}
 */
 export const onRmsDataChange = (callback) => subscribeToStateChange(
    WRAPPER_RMS_STATE_KEY,
    callback
);

/**
 * Fires the callback when serial number in state changed.
 * @param {Function} callback   Function which is run when serial number state changed.
 * @return {void}
 */
export const onSerialDataChange = (callback) => subscribeToStateChange(
    WRAPPER_SERIAL_STATE_KEY,
    callback
);

/**
 * Checks if the user has old Kambi bets
 * @return {Boolean} Wheter customer has Kambi bets or not
 */
export const hasKambiBets = () => Boolean(getState(USER_HAS_KAMBI_BETS));

/**
 * Checks if the user has restriced apps
 * @return {Array} List of user's restricted apps
 */
export const getRestrictedApps = () => JSON.parse(sessionStorage.getItem(RESTRICTED_APPS_KEY));

/**
 * Set user's restricted apps in session storage
 * @param {Array} restrictedApps of user's restricted apps
 * @return {void}
 */
export const setRestrictedApps = (restrictedApps) => {
    sessionStorage.setItem(RESTRICTED_APPS_KEY, JSON.stringify(restrictedApps));
};

/**
 * Checks if user is restricted for poker games
 * @return {Boolean} Whether customer is blocked from poker
 */
export const isPokerBlocked = () => {
    const restricted_apps = getRestrictedApps();
    if (restricted_apps !== null && Array.isArray(restricted_apps)) {
        return restricted_apps.includes(POKER_BLAST_WIDGET);
    }

    return false;
};

/**
 * Checks if the user should see instruction cards
 * @return {Array} List of instruction cards user should see
 */
export const getInstructionCards = () => getState(USER_INSTRUCTION_CARDS) || [];

/**
 * Fires the callback when Instruction Cards list changed.
 * @param {Function} callback   Function which is run when Instruction Cards list changed.
 * @return {void}
 */
export const onInstructionCardsChange = (callback) => subscribeToStateChange(
    USER_INSTRUCTION_CARDS,
    callback
);

/**
 * Sets the user's instruction cards dismisses
 * @param {Array} List of instruction cards
 * @return {void}
 */
export const setInstructionCards = (List) => {
    setState(USER_INSTRUCTION_CARDS, List);
};

/**
 * Sets the user's favourite sports
 * @param {Array} sportsIds favourite sports ids
 * @return {void}
 */
export const setFavouriteSports = (sportsIds) => {
    setState(USER_FAVOURITE_SPORTS, sportsIds);
};

/**
 * Gets the user's favourite sports
 * @return {Array} favourite sports ids.
 */
export const getFavouriteSports = () => getState(USER_FAVOURITE_SPORTS) || [];

/**
 * stores true if it is the first time the user has logged in.
 * @param {Boolean} isFirstLogin true if is users first time logging in.
 * @return {void}
 */
export const setIsFirstLogin = (isFirstLogin = false) => {
    setState(USER_IS_FIRST_LOGIN, isFirstLogin);
};

/**
 * Gets stored state of isFirstLogin
 * @return {Boolean} value of isFirstLogin
 */
export const isFirstLogin = () => getState(USER_IS_FIRST_LOGIN);

/**
 * stores true if the wrapper has sent us PostRegistrationDepositClosed.
 * meaning they are finished with their post login initialization processes.
 * @param {Boolean} didReceivePRDCMessage true if wrapper sent message.
 * @return {void}
 */
export const setDidReceivePRDCMessage = (didReceivePRDCMessage = false) => {
    setState(WRAPPER_PRDC_MESSAGE_RECIVED_KEY, didReceivePRDCMessage);
};

/**
 * Gets stored state of didReceivePRDCMessage
 * @return {Boolean} true if wrapper sent us PostRegistrationDepositClosed
 * meaning they are finished with their post logininitialization processes.
 */
export const didReceivePRDCMessage = () => getState(WRAPPER_PRDC_MESSAGE_RECIVED_KEY);

/**
 * Checks if the user is allowed eachway
 * @return {boolean} If user is allowed to view eachway
 */
export const getEachwayAllowed = () => getState(USER_EACHWAY_ALLOWED);

/**
 * Sets the user's eachway allowed status
 * @param {boolean} data if allowed or not
 * @return {void}
 */
export const setEachwayAllowed = (data) => {
    setState(USER_EACHWAY_ALLOWED, data);
};

/**
 * Sets BOG restriction flag
 * @param {Boolean} isRestricted negated is_bog_allowed from user restrictions
 * @return {void}
 */
export const setIsBogRestricted = (isRestricted) => {
    setState(USER_BOG_RESTRICTION, isRestricted);
};

/**
 * Gets BOG restriction flag
 * @return {Boolean} isRestricted negated is_bog_allowed from user restrictions
 */
export const getIsBogRestricted = () => getState(USER_BOG_RESTRICTION);

/*
 * Sets the regulation data associated with the customer account
 * @param {Object} data The regulation type object associated
 * @return {void}
 */
export const setRegulationTypeData = (data) => {
    setState(REGULATION_TYPE_DATA_STATE_KEY, data);
};

/**
 * Returns the regulation data value requested in key parameter
 * associated with the current customer account
 * @param {String} key The key of the value we want to retrieve
 * @return {*} The value of the key parameter in this regulation
 */
export const getRegulationTypeData = (key) => {
    const regulation = getState(REGULATION_TYPE_DATA_STATE_KEY);

    return !regulation ? undefined : regulation[key];
};

/**
 * Returns user regulation tax_on_returns
 * @return {*} The value of tax_on_returns string|null|undefined
 */
export const getUserTaxOnReturns = () => getRegulationTypeData(REGULATION_TAX_ON_RETURNS_KEY);

/**
 * Returns user regulation betbuilder_region
 * @return {*} The value of betbuilder_region string|null
 */
export const getUserBetbuilderRegion = () => getRegulationTypeData(REGULATION_BETBUILDER_REGION_KEY);

/**
 * Is user login restricted by regulation type id
 * @return {Boolean} is restricted
 */
export const isLoginRestrictedByRegulationType = () => getRegulationTypeData(REGULATION_LOGIN_RESTRICTED_KEY);

/**
 * Should the login be hidden from user?
 * This is just a UI check - enforcement of this policy is by the wrapper
 * @see isLoginRestrictedByRegulationType()
 * @returns {Boolean} hide
 */
export const shouldHideLogin = () => (
    !isUserLoggedIn()
    && isLoginRestrictedByRegulationType()
    && getIsMobileWrapperFlag()
    && !getIsHybridWrapperFlag()
);

/**
 * Gets the Geo Check Status
 * @return {String|null} Current status
 */
export const getGeoCheckStatus = () => getState(WRAPPER_GEO_CHECK_STATUS_KEY) || null;

/**
 * Geo Check Timer
 */
let geoCheckTimer;

/**
 * Sets the Geo Check Status.
 *
 * If the received status is "in_progress" it will automatically reset the status
 * to null after configured time.
 * We need the timeout to cover a case when Geo Check is slow.
 *
 * @param {String|Object|null} status
 *                             String: in case of Geo Check is in progress
 *                             Object: data with Geo Check status
 *                             null in case of geo check status is not known
 * @return {void}
 */
export const setGeoCheckStatus = (status) => {
    setState(
        WRAPPER_GEO_CHECK_STATUS_KEY, status
    );
    clearTimeout(geoCheckTimer);
    if (status === 'in_progress') {
        geoCheckTimer = setTimeout(
            () => setGeoCheckStatus(null),
            getGeoCheckTimeout() * 1000
        );
    }
};

/**
 * Fires the callback when Geo Check Status changed
 * @param {Function} callback   Function which is run when Geo Check status changed.
 * @return {void}
 */
export const onGeoCheckStatusChange = (callback) => subscribeToStateChange(
    WRAPPER_GEO_CHECK_STATUS_KEY, callback
);

/**
 * Sets all the user balances
 *
 * @param {Object} balances Balance from UC
 * @returns {void}
 */
export const setBalances = (balances) => {
    setState(
        USER_BALANCES_KEY, balances
    );
};

/**
 * Fires a callback when the user balance is updated
 * @param {Function} callback Function run when the user balance changes
 * @returns {void}
 */
export const onBalancesChange = (callback) => subscribeToStateChange(
    USER_BALANCES_KEY, callback
);

/**
 * Gets the user balances
 * @returns {Object} The users balances or empty if undefined
 */
export const getBalances = () => getState(USER_BALANCES_KEY) || {};

/**
 * Gets user preference for accepting odds changes on betslip
 * @return {Boolean} the flag for accepting odds
 */
export const getCustomerAcceptOddsFlag = () => getState(USER_ACCEPT_ODDS_CHANGE_KEY);

/**
 * Fires the callback when customer accept odds change flag in state changed.
 * @param {Function} callback   Function which is run when accept odds flag changed.
 * @return {void}
 */
export const onCustomerAcceptOddsFlagChange = (callback) => subscribeToStateChange(
    USER_ACCEPT_ODDS_CHANGE_KEY,
    callback
);

/**
 * Sets user preference for accepting odds changes on betslip
 * @param {Boolean} toggleState the value for odds acceptance flag
 * @return {void}
 */
export const setCustomerAcceptOddsFlag = (toggleState) => setState(USER_ACCEPT_ODDS_CHANGE_KEY, toggleState);

/**
 * Gets user preference for accepting cashout confirmation in bet history
 * @return {Boolean} the flag for accepting cashout confirmation
 */
export const getCustomerConfirmCashoutFlag = () => getState(USER_CONFIRM_CASHOUT_KEY);

 /**
  * Sets user preference for accepting cashout confirmation in bet history
  * @param {Boolean} toggleState the value for cashout confirmation
  * @return {void}
  */
export const setCustomerConfirmCashoutFlag = (toggleState) => setState(USER_CONFIRM_CASHOUT_KEY, toggleState);

/**
 * Gets status of the favourite_sports_onboarding_page_enabled.
 * Flag is updatable via CMS on the operations > Switches/Settings
 * @return {Boolean} favourite_sports_onboarding_page_enabled
 */
export const getOnBoardingPageEnabled = () => getState(ONBOARDING_PAGE_ENABLED_KEY);

/**
 * Sets status of the favourite_sports_onboarding_page_enabled.
 * Flag is updatable via CMS on the operations > Switches/Settings
 * @param {Boolean} isEnabled value of onboardingPage:enabled
 * @return {void}
 */
export const setOnBoardingPageEnabled = (isEnabled = true) => setState(ONBOARDING_PAGE_ENABLED_KEY, Boolean(isEnabled));

/**
 * Calls callback when favourite sport info is updated in the state.
 * @param {Function} callback Function which is run when state changed.
 * @returns {Function} unsubscribe function
 */
export const onFavouriteSportWasUpdated = (callback) => subscribeToStateChange(USER_FAVOURITE_SPORTS, callback);

/**
 * Get the page correlation id provided by Wrapper
 * @return {String} Wrapper's Page Correlation Id
 */
export const getWrapperPageCorrelationId = () => getState(WRAPPER_PAGE_CORRELATION_ID_STATE_KEY) || '';
